import { useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  registerCurrentSession,
  checkSessionRevoked,
  updateLastActive,
} from '../lib/sessionDevice';

const UPDATE_INTERVAL_MS = 5 * 60 * 1000;   // update last_active every 5 min
const RETRY_DELAY_MS     = 10 * 1000;        // retry registration after 10s if first attempt failed
const MAX_RETRIES        = 3;

export function useSessionManager() {
  const { user, signOut } = useAuth();
  const registeredRef  = useRef(false);
  const lastUserIdRef  = useRef<string | null>(null);
  const retryCountRef  = useRef(0);
  const retryTimerRef  = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ── Clear retry timer on unmount ──────────────────────────────────────────
  useEffect(() => {
    return () => {
      if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
    };
  }, []);

  // ── Register session when user first logs in ──────────────────────────────
  const tryRegister = useCallback(async (userId: string) => {
    const success = await registerCurrentSession(userId);

    if (!success && retryCountRef.current < MAX_RETRIES) {
      retryCountRef.current += 1;
      console.warn(
        `[SessionManager] registration failed — retrying in ${RETRY_DELAY_MS / 1000}s ` +
        `(attempt ${retryCountRef.current}/${MAX_RETRIES})`
      );
      retryTimerRef.current = setTimeout(() => tryRegister(userId), RETRY_DELAY_MS);
      return;
    }

    if (success) {
      registeredRef.current = true;
      retryCountRef.current = 0;
    }
  }, []);

  useEffect(() => {
    // Reset registration state when the user changes (logout → login)
    if (!user) {
      registeredRef.current = false;
      lastUserIdRef.current = null;
      retryCountRef.current = 0;
      if (retryTimerRef.current) {
        clearTimeout(retryTimerRef.current);
        retryTimerRef.current = null;
      }
      return;
    }

    // Avoid re-registering for the same user in the same session
    if (registeredRef.current && lastUserIdRef.current === user.id) return;

    lastUserIdRef.current = user.id;

    // NOTE: We do NOT check for revocation here — that would prevent a revoked
    // device from ever logging back in. Revocation is only checked on tab focus
    // (while the session is actively running).
    tryRegister(user.id);
  }, [user, tryRegister]);

  // ── Periodically update last_active_at ───────────────────────────────────
  useEffect(() => {
    if (!user) return;

    const timer = setInterval(() => {
      if (user) updateLastActive(user.id);
    }, UPDATE_INTERVAL_MS);

    return () => clearInterval(timer);
  }, [user]);

  // ── Re-check revocation on tab focus ─────────────────────────────────────
  // This detects if another device revoked THIS session while the user was
  // actively using the app.
  useEffect(() => {
    if (!user) return;

    const handleFocus = async () => {
      // Only check if already registered — new devices should not be kicked
      if (!registeredRef.current) return;

      const revoked = await checkSessionRevoked(user.id);
      if (revoked) {
        console.warn('[SessionManager] revocation detected on focus — signing out');
        await signOut();
      }
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [user, signOut]);
}
